import { useEffect, useState, useCallback } from 'react';
import type {
  OperationVariables,
  DocumentNode,
  ApolloError,
  QueryResult,
  LazyQueryResultTuple,
  ApolloClient,
  NormalizedCacheObject,
  LazyQueryExecFunction,
} from '@apollo/client';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import type { WatchQueryFetchPolicy } from '@apollo/client/core/watchQueryOptions';
import type { DefinitionNode } from 'graphql';
import { usePreviousValue } from '@wistia/vhs';
import isEqual from 'lodash/isEqual';
import type { Nilable } from '~/utilities/type-guards';
import { isNil, isNotNil } from '~/utilities/type-guards';
import { reportError } from '~/utilities/errorReporting';
import { useSearchParams } from '~/hooks/useSearchParams';
import { useBottlerContext } from './BottlerApolloContext';

const LONG_LOAD_TIMEOUT = 200;

interface Options<TVariables> {
  persistDataBetweenQueries?: boolean;
  errorPolicy?: 'all' | 'ignore' | 'none';
  skip?: boolean;
  variables?: TVariables;
  onCompleted?: (data: unknown) => void;
  onError?: (error: unknown) => void;
  timeout?: number;
  client?: unknown;
  pollInterval?: number;
  context?: Record<string, unknown>;
}

interface FetchOptions<TVariables> extends Options<TVariables> {
  fetchPolicy?: WatchQueryFetchPolicy;
}

export interface QueryReturn<TData, TVariables extends OperationVariables>
  extends QueryResult<TData, TVariables> {
  isLoading: boolean;
  isLongLoad: boolean;
}

export type LazyQueryReturn<TData, TVariables extends OperationVariables> = [
  LazyQueryExecFunction<TData, TVariables>,
  QueryReturn<TData, TVariables>,
];

const processReturn = <TData, TVariables extends OperationVariables>(
  hookReturn: LazyQueryResultTuple<TData, TVariables> | QueryResult<TData, TVariables>,
): LazyQueryReturn<TData, TVariables> | [undefined, QueryReturn<TData, TVariables>] => {
  if (hookReturn instanceof Array) {
    const [performQuery, { loading, ...rest }] = hookReturn;
    return [
      performQuery,
      { ...rest, loading, isLoading: loading, isLongLoad: false },
    ] as LazyQueryReturn<TData, TVariables>;
  }

  const { loading, ...rest } = hookReturn;
  return [undefined, { ...rest, loading, isLoading: loading, isLongLoad: false }] as [
    undefined,
    QueryReturn<TData, TVariables>,
  ];
};

const useEnhancedQuery = <
  TVariables extends OperationVariables,
  TData,
  THook extends (
    ...args: unknown[]
  ) => LazyQueryResultTuple<TData, TVariables> | QueryResult<TData, TVariables>,
  TReturn = THook extends (...args: unknown[]) => LazyQueryResultTuple<TData, TVariables>
    ? LazyQueryReturn<TData, TVariables>
    : QueryReturn<TData, TVariables>,
>(
  query: DocumentNode,
  hook: THook,
  options: FetchOptions<TVariables>,
): TReturn => {
  const [data, setData] = useState<TData | undefined>();
  const [error, setError] = useState<ApolloError | undefined>();
  const [dataAwareIsLoading, setDataAwareIsLoading] = useState(false);
  const [isLongLoad, setIsLongLoad] = useState(false);
  const previousOptions = usePreviousValue(options);
  const [searchParams] = useSearchParams();
  const xRequestId = searchParams.get('xRequestId');
  const providedOptions = { ...options };

  if (isNotNil(xRequestId)) {
    providedOptions.context = providedOptions.context ?? {};
    providedOptions.context.headers = {
      'x-request-id': xRequestId,
    };
  }

  const hookResult = processReturn<TData, TVariables>(
    hook(query, {
      ...providedOptions,
      onCompleted: (result: TData) => {
        setData(result);
        setDataAwareIsLoading(false);
        setIsLongLoad(false);
        if (options.onCompleted) {
          options.onCompleted(result);
        }
      },
      onError: (result: ApolloError) => {
        setData(undefined);
        setError(result);
        setDataAwareIsLoading(false);
        setIsLongLoad(false);
        if (options.onError) {
          options.onError(result);
        }

        const [queryName] = query.definitions.map(
          (definitionNode: DefinitionNode) =>
            'name' in definitionNode && definitionNode.name?.value,
        );
        reportError(result, {
          queryName,
          variables: options.variables,
          pillar: 'analyze',
          product: 'analytics',
        });
      },
    }),
  );

  const performQuery = hookResult[0];
  const { data: clearableData, isLoading, refetch: originalRefetch, ...rest } = hookResult[1];

  useEffect(() => {
    if (!previousOptions) {
      return;
    }
    if (!isEqual(options.variables, previousOptions.variables)) {
      setError(undefined);
    }
  }, [options, previousOptions]);

  useEffect(() => {
    let timer: Nilable<ReturnType<typeof setTimeout>> = null;
    const timeout = options.timeout ?? LONG_LOAD_TIMEOUT;
    if (isLoading) {
      setDataAwareIsLoading(true);
      timer = setTimeout(() => {
        if (dataAwareIsLoading) {
          setIsLongLoad(true);
        }
      }, timeout);
    }

    return () => {
      if (isNil(timer)) {
        return;
      }
      clearTimeout(timer);
    };
  }, [isLoading, dataAwareIsLoading, options.timeout]);

  const enhancedRefetch = useCallback(
    async (variables?: Partial<TVariables>) => {
      setDataAwareIsLoading(true);
      setIsLongLoad(false);
      try {
        const refetchResult = await originalRefetch(variables);
        setDataAwareIsLoading(false);
        return refetchResult;
      } catch (err) {
        setError(err as ApolloError);
        setDataAwareIsLoading(false);
        setIsLongLoad(false);
        return undefined;
      }
    },
    [originalRefetch],
  );

  const returnValue = {
    ...rest,
    data: options.persistDataBetweenQueries ? data : clearableData,
    isLoading: dataAwareIsLoading,
    // correct for race condition where isLongLoad is true but clearableData was returned
    isLongLoad: isLongLoad && isNil(clearableData),
    error,
    clearData: () => setData(undefined),
    refetch: enhancedRefetch,
  };

  if (isNil(performQuery)) {
    return returnValue as TReturn;
  }

  return [performQuery, returnValue] as TReturn;
};

/**
 * A custom hook for making queries using Bottler.
 *
 * @param {DocumentNode | string} query - The GraphQL query string.
 * @param {Object} providedOptions - Custom options for the query.
 */
export const useBottlerQuery = <
  TData = unknown,
  TVariables extends OperationVariables = Record<string, unknown>,
>(
  query: DocumentNode,
  providedOptions: FetchOptions<TVariables> = {},
): QueryReturn<TData, TVariables> => {
  const client = useBottlerContext();
  const defaultOptions = { client, persistDataBetweenQueries: false };
  const options = { ...defaultOptions, ...providedOptions };

  return useEnhancedQuery<TVariables, TData, typeof useQuery, QueryReturn<TData, TVariables>>(
    query,
    useQuery,
    options,
  );
};

/**
 * A custom hook for making lazy queries using Bottler.
 *
 * @param {DocumentNode | string} query - The GraphQL query string.
 * @param {Object} providedOptions - Custom options for the query.
 */
export const useLazyBottlerQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode,
  providedOptions: Options<TVariables> = {},
): LazyQueryReturn<TData, TVariables> => {
  const client = useBottlerContext();
  const defaultOptions = { client, persistDataBetweenQueries: false };
  const options = { ...defaultOptions, ...providedOptions };

  return useEnhancedQuery<
    TVariables,
    TData,
    typeof useLazyQuery,
    LazyQueryReturn<TData, TVariables>
  >(query, useLazyQuery, options);
};

export const useBottlerMutation = <TData = unknown, TVariables = Record<string, unknown>>(
  query: DocumentNode,
  options: Options<TVariables> = {},
): ReturnType<typeof useMutation> => {
  const client = useBottlerContext() as ApolloClient<NormalizedCacheObject>;
  const mutationOptions = { ...options, client };

  // eslint-disable-next-line rulesdir/no-use-mutation -- Bottler isn't compatible with graphql-codegen
  return useMutation<TData, TVariables>(query, mutationOptions);
};
